import { DesktopOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import {
    isInstallationActive,
    listHardwareInstallations,
    revokeHardwareInstallation,
    type HardwareInstallation,
} from '../utils/hardwareServiceInstallations';

const { Text, Title } = Typography;

/**
 * Administration of paired hardware service workstations.
 *
 * Revocation is enforced centrally — the workstation keeps its key, since nothing can
 * reach into it and delete one — so revoking here stops the backend building any
 * further label jobs for that machine.
 */
export default function HardwareInstallations() {
    const { t } = useTranslation();
    const { API_BASE_URL, token } = useAuth();

    const [installations, setInstallations] = useState<HardwareInstallation[]>([]);
    const [loading, setLoading] = useState(false);
    const [revoking, setRevoking] = useState<string | null>(null);

    const authHeaders = { Authorization: `Bearer ${token}` };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setInstallations(
                await listHardwareInstallations(API_BASE_URL, { Authorization: `Bearer ${token}` }),
            );
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, token]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const handleRevoke = async (installationId: string) => {
        setRevoking(installationId);
        try {
            const revoked = await revokeHardwareInstallation(
                API_BASE_URL,
                authHeaders,
                installationId,
            );

            if (revoked) {
                message.success(t('configuration.workstations.revoked'));
                await refresh();
            } else {
                message.error(t('configuration.workstations.revokeFailed'));
            }
        } finally {
            setRevoking(null);
        }
    };

    const formatMoment = (value: string | null): string =>
        value === null ? '—' : new Date(value).toLocaleString();

    const columns = [
        {
            title: t('configuration.workstations.cashPoint'),
            dataIndex: 'cashPointName',
            key: 'cashPointName',
            render: (name: string | null, row: HardwareInstallation) =>
                name ?? `#${row.cashPointId}`,
        },
        {
            title: t('configuration.workstations.installationId'),
            dataIndex: 'installationId',
            key: 'installationId',
            render: (id: string) => <Text code>{id}</Text>,
        },
        {
            title: t('configuration.workstations.pairedAt'),
            dataIndex: 'pairedAt',
            key: 'pairedAt',
            render: (value: string) => formatMoment(value),
        },
        {
            title: t('configuration.workstations.status'),
            key: 'status',
            render: (_unused: unknown, row: HardwareInstallation) =>
                isInstallationActive(row) ? (
                    <Tag color="green">{t('configuration.workstations.active')}</Tag>
                ) : (
                    <Tag color="red">
                        {t('configuration.workstations.revokedAt', {
                            when: formatMoment(row.revokedAt),
                        })}
                    </Tag>
                ),
        },
        {
            title: '',
            key: 'actions',
            render: (_unused: unknown, row: HardwareInstallation) =>
                isInstallationActive(row) ? (
                    <Popconfirm
                        title={t('configuration.workstations.confirmRevoke')}
                        description={t('configuration.workstations.confirmRevokeDescription')}
                        okText={t('configuration.workstations.revoke')}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleRevoke(row.installationId)}
                    >
                        <Button
                            danger
                            size="small"
                            loading={revoking === row.installationId}
                            data-testid={`revoke-${row.installationId}`}
                        >
                            {t('configuration.workstations.revoke')}
                        </Button>
                    </Popconfirm>
                ) : null,
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
                <Title level={4} style={{ marginBottom: 4 }}>
                    <DesktopOutlined style={{ marginRight: 8 }} />
                    {t('configuration.workstations.title')}
                </Title>
                <Text type="secondary">{t('configuration.workstations.description')}</Text>
            </div>

            <Table
                rowKey="installationId"
                data-testid="workstations-table"
                loading={loading}
                dataSource={installations}
                columns={columns}
                pagination={false}
                locale={{ emptyText: t('configuration.workstations.empty') }}
            />
        </Space>
    );
}
